import { keyBy } from 'lodash';
import { Permission } from '../../graphQL';
import { AccommodationsSection } from './Section/AccommodationsSection';
import { BeaconResourcesSection } from './Section/BeaconResourcesSection';
import { CampusResourcesSection } from './Section/CampusResourcesSection';
import { CareAndCollaborationSection } from './Section/CareAndCollaborationSection';
import { ContinuityOfCareSection } from './Section/ContinuityOfCareSection';
import { EmergencyProtocolSection } from './Section/EmergencyProtocolSection';
import { MedicalSection } from './Section/MedicalSection';
import {
  titleAccommodations,
  titleBeaconResources,
  titleCampusResources,
  titleCareAndCollaboration,
  titleContinuityOfCare,
  titleEmergencyProtocol,
  titleMedical,
} from './Section/sectionConstants';
import { EditableSectionRowInfo } from './Section/types';

export enum HandbookPanelKey {
  careAndCollaboration = 'careAndCollaboration',
  campusResources = 'campusResources',
  accomodations = 'accomodations',
  continuityOfCare = 'continuityOfCare',
  medical = 'medical',
  emergencyProtocol = 'emergencyProtocol',
  beaconResources = 'beaconResources',
}

type PanelsType = {
  key: HandbookPanelKey;
  title: string;
  editPermission: Permission;
  Component: (props: any) => JSX.Element;
  editableSectionRowInfo: EditableSectionRowInfo[];
  hideInVideo?: boolean;
};

const HANDBOOK_PANELS: Readonly<PanelsType>[] = [
  {
    key: HandbookPanelKey.careAndCollaboration,
    editPermission: Permission.OrganizationHandbookEdit,
    title: titleCareAndCollaboration,
    Component: CareAndCollaborationSection,
    editableSectionRowInfo: [
      {
        label: 'Session Limits Renew Each Academic Year?',
        editInputKey: 'sessionLimitsRenew',
        displayKey: 'sessionLimitsRenew',
      },
      {
        label: 'No Shows and Late Cancellations count toward session limit?',
        info: 'Do patient no-shows and late cancellations (<24hr before appt) result in a deduction of a session?',
        editInputKey: 'noShowsCountForSessionLimits',
        displayKey: 'noShowsCountForSessionLimits',
      },
      {
        label: 'Default Therapy Cadence',
        editInputKey: 'defaultTherapyCadence',
        displayKey: 'defaultTherapyCadence',
      },
      {
        label: 'Co-pay charged by partner for Mantra services?',
        info: 'Does the partner charge a co-pay for Mantra services?',
        editInputKey: 'coPayChargedByPartner',
        displayKey: 'coPayChargedByPartner',
      },
    ],
  },
  {
    key: HandbookPanelKey.campusResources,
    editPermission: Permission.OrganizationCampusResourcesEdit,
    title: titleCampusResources,
    Component: CampusResourcesSection,
    editableSectionRowInfo: [],
  },
  {
    key: HandbookPanelKey.accomodations,
    editPermission: Permission.MantraAdmin,
    title: titleAccommodations,
    Component: AccommodationsSection,
    editableSectionRowInfo: [
      {
        label: 'Requesting academic accommodations',
        editInputKey: 'requestingAcademicAccomodations',
        displayKey: 'requestingAcademicAccomodations',
      },
      {
        label: 'Full ADHD evaluation',
        editInputKey: 'fullADHDEvaluation',
        displayKey: 'fullADHDEvaluation',
      },
      {
        label: 'Protocol for obtaining Medical Leave documentation',
        editInputKey: 'protocolForMedicalLeaveDoc',
        displayKey: 'protocolForMedicalLeaveDoc',
      },
    ],
  },
  {
    key: HandbookPanelKey.continuityOfCare,
    editPermission: Permission.OrganizationHandbookEdit,
    title: titleContinuityOfCare,
    Component: ContinuityOfCareSection,
    editableSectionRowInfo: [
      {
        label: 'Summer Sponsored Break: Who is eligible?',
        editInputKey: 'summerBreakEligibility',
        displayKey: 'summerBreakEligibility',
      },
      {
        label: 'Winter Sponsored Break: Who is eligible?',
        editInputKey: 'winterBreakEligibility',
        displayKey: 'winterBreakEligibility',
      },
    ],
  },
  {
    key: HandbookPanelKey.medical,
    editPermission: Permission.OrganizationHandbookEdit,
    title: titleMedical,
    Component: MedicalSection,
    editableSectionRowInfo: [
      {
        label: 'Labs',
        editInputKey: 'labsNearCampus',
        displayKey: 'labsNearCampus',
      },
      {
        label: 'EKGs',
        editInputKey: 'ekgsNearCampus',
        displayKey: 'ekgsNearCampus',
      },
      {
        label: 'Vitals',
        editInputKey: 'vitalsNearCampus',
        displayKey: 'vitalsNearCampus',
      },
    ],
  },
  {
    key: HandbookPanelKey.emergencyProtocol,
    editPermission: Permission.OrganizationHandbookEdit,
    title: titleEmergencyProtocol,
    Component: EmergencyProtocolSection,
    editableSectionRowInfo: [
      {
        label: 'High level protocol for managing emergencies',
        editInputKey: 'highLevelProtocolManagingEmergency',
        displayKey: 'highLevelProtocolManagingEmergency',
      },
      {
        label: 'Campus safety/police number',
        editInputKey: 'campusSafetyPoliceNumber',
        displayKey: 'campusSafetyPoliceNumber',
      },
      {
        label: 'Supporting Clinician',
        editInputKey: 'supportingClinician',
        displayKey: 'supportingClinician',
      },
      {
        label: 'Hospitalization',
        editInputKey: 'hospitalization',
        displayKey: 'hospitalization',
      },
      {
        label: 'Main point of contact to inform about the crisis',
        editInputKey: 'mainPointOfContactCrisis',
        displayKey: 'mainPointOfContactCrisis',
      },
      {
        label: 'On-Campus Crisis Team',
        editInputKey: 'campusCrisisTeamInProduct',
        displayKey: 'campusCrisisTeamInProduct',
      },
    ],
  },
  {
    key: HandbookPanelKey.beaconResources,
    editPermission: Permission.OrganizationSaiResourcesLibraryEdit,
    title: titleBeaconResources,
    Component: BeaconResourcesSection,
    editableSectionRowInfo: [],
  },
];

export const panelLookup = keyBy(HANDBOOK_PANELS, panel => panel.key);

export const getHandbookPanels = ({
  excludePanels,
}: { excludePanels?: HandbookPanelKey[] } = {}) => {
  return excludePanels
    ? HANDBOOK_PANELS.filter(panel => !excludePanels.includes(panel.key))
    : HANDBOOK_PANELS;
};
